/**
 * SchedulePermissions - Self-contained permission evaluator for schedule-related actions
 *
 * This class receives permission rules from the API and evaluates
 * whether a user has permission to perform schedule-related actions.
 * No external dependencies required - can be used in any codebase.
 *
 * Supported actions:
 * - scheduleCreate, scheduleRead, scheduleEdit, scheduleEnable, schedulePageAccess
 * - taskCreate, taskRead, taskEdit, taskDelete
 * - subtaskLink, subtaskChangeLead
 */
export default class SchedulePermissions {
  /**
   * @param {Object} config - Configuration object
   * @param {Array} config.permissionRules - Array of permission rule objects from API
   *   Each rule should have: { action, permissionConditions, permissionOptions, resource }
   * @param {string} config.userId - Current user's ID
   * @param {string} config.teamId - Current team ID
   * @param {string} config.defaultTeamId - User's default team ID
   * @param {Array} config.teamTags - Current team's tags
   */
  constructor({ permissionRules = [], userId = '', teamId = '', defaultTeamId = '', teamTags = [] } = {}) {
    this.userId = userId;
    this.teamId = teamId;
    this.defaultTeamId = defaultTeamId;
    this.teamTags = teamTags;

    // Initialize permission rules with context
    this.permissionRules = this._initializeRules(permissionRules);

    // Build action map for efficient lookups
    this.permissionRulesByActionMap = this._buildActionMap();
  }

  /**
   * Initialize permission rules with user context
   * @private
   * @param {Array} rules - Raw permission rules from API
   * @returns {Array} Initialized rules
   */
  _initializeRules(rules) {
    if (!Array.isArray(rules)) {
      console.warn('permissionRules must be an array, received:', typeof rules);
      return [];
    }

    return rules.map(rule => ({
      ...rule,
      _options: {
        userId: this.userId,
        teamId: this.teamId,
        defaultTeamId: this.defaultTeamId,
        teamTags: this.teamTags,
      },
    }));
  }

  /**
   * Build a map of permission rules grouped by action
   * @private
   * @returns {Object} Map of action -> permission rules
   */
  _buildActionMap() {
    const map = {};

    this.permissionRules.forEach(rule => {
      const { action } = rule;

      if (!map[action]) {
        map[action] = [];
      }

      map[action].push(rule);
    });

    return map;
  }

  /**
   * Evaluate if a value matches a target (implements lodash intersection logic)
   * @private
   * @param {*} value - The condition value
   * @param {*} target - The target value from schedule
   * @param {boolean} strict - Whether to use strict matching
   * @returns {boolean} Whether the condition matches
   */
  _evaluate(value, target, strict = false) {
    switch (true) {
      case typeof value === 'string':
        return (!strict && !value.length) || value === target;
      case typeof value === 'number':
        return value === target;
      case typeof value === 'boolean':
        return value === target;
      case Array.isArray(value): {
        if (!strict && !value.length) {
          return true;
        }
        if (Array.isArray(target)) {
          // Intersection logic
          return value.some(v => target.includes(v));
        }
        return value.includes(target);
      }
      case typeof value === 'object' && value !== null:
        return JSON.stringify(value) === JSON.stringify(target);
      default:
        return true;
    }
  }

  /**
   * Evaluate a single permission condition
   * @private
   * @param {Object} condition - Permission condition object
   * @param {Object} viewObject - Object to evaluate against (schedule, task, or subtask)
   * @param {Object} options - Context options (userId, teamId, etc.)
   * @param {string} resource - The resource type being evaluated (schedule, task, subtask)
   * @returns {boolean} Whether the condition is met
   */
  _evaluateCondition(condition, viewObject, options, resource = 'schedule') {
    const { type, value } = condition;

    switch (type) {
      case 'schedule':
        // For schedules, check id; for tasks/subtasks, check scheduleId
        if (resource === 'schedule') {
          return this._evaluate(value, viewObject?.id);
        }
        return this._evaluate(value, viewObject?.scheduleId);
      case 'scheduleTag':
        // Tags property is used consistently across schedule, task, and subtask
        return this._evaluate(value, viewObject?.tags);
      case 'myTeam':
        return this._evaluate(options.teamId, viewObject?.teamId);
      case 'matchesMyTeamTag':
        return this._evaluate(options.teamTags, viewObject?.tags, true);
      case 'unassigned':
        return this._evaluate('', viewObject?.assignedToTeamId, true);
      case 'assignedToMyTeam':
        return this._evaluate(options.teamId, viewObject?.assignedToTeamId);
      case 'controlledByTeamTag':
        return this._evaluate(value, viewObject?.controlledByTeamTags);
      case 'controlledByMyTeam':
        return this._evaluate(options.teamId, viewObject?.controlledByTeamId);
      case 'leadByMyTeam':
        // Used by subtask permissions - checks if subtask is led by user's team
        return this._evaluate(options.teamId, viewObject?.leadByTeamId);
      case 'createdByMe':
        return this._evaluate(options.userId, viewObject?.createdByUserId);
      default:
        return true;
    }
  }

  /**
   * Get the resource type for an action
   * @private
   * @param {string} action - Action name
   * @returns {string} Resource type (schedule, task, subtask, or empty string)
   */
  _getResourceForAction(action) {
    const actionResourceMap = {
      scheduleCreate: '',
      scheduleRead: 'schedule',
      scheduleEdit: 'schedule',
      scheduleEnable: 'schedule',
      schedulePageAccess: '',
      taskCreate: 'schedule',
      taskRead: 'task',
      taskEdit: 'task',
      taskDelete: 'task',
      subtaskLink: 'subtask',
      subtaskChangeLead: 'subtask',
    };
    return actionResourceMap[action] || '';
  }

  /**
   * Evaluate a permission rule against a view object (schedule, task, or subtask)
   * @private
   * @param {Object} rule - Permission rule
   * @param {Object|null} viewObject - Object to evaluate against (schedule, task, or subtask)
   * @returns {boolean|Array} Permission options if rule matches, false otherwise
   */
  _evaluateRule(rule, viewObject) {
    const { permissionConditions = [], permissionOptions = [], _options } = rule;

    // Check if rule has team-specific restrictions
    const isTeamSpecific = this._isTeamSpecificAction(rule.action);
    const isDefaultTeam = _options.teamId === _options.defaultTeamId;

    // If action is team-specific and not on default team, skip evaluation
    if (isTeamSpecific && !isDefaultTeam) {
      return false;
    }

    // Get the resource type for condition evaluation
    const resource = this._getResourceForAction(rule.action);

    // If viewObject is provided, validate all conditions
    if (viewObject !== null && viewObject !== undefined) {
      const allConditionsMet = permissionConditions.every(condition =>
        this._evaluateCondition(condition, viewObject, _options, resource),
      );

      if (!allConditionsMet) {
        return false;
      }
    } else if (permissionConditions.length > 0) {
      // If no viewObject provided but conditions exist, evaluate without it
      const allConditionsMet = permissionConditions.every(condition =>
        this._evaluateCondition(condition, null, _options, resource),
      );

      if (!allConditionsMet) {
        return false;
      }
    }

    // Return permission options
    return permissionOptions.map(option => ({
      type: option.type,
      value: option.value,
    }));
  }

  /**
   * Check if an action is team-specific
   * @private
   * @param {string} action - Action name
   * @returns {boolean}
   */
  _isTeamSpecificAction(action) {
    const teamSpecificActions = [
      'scheduleCreate',
      'scheduleEdit',
      'scheduleEnable',
      'taskCreate',
      'taskEdit',
      'taskDelete',
      'subtaskLink',
      'subtaskChangeLead',
    ];
    return teamSpecificActions.includes(action);
  }

  /**
   * Evaluate permission rules using privilege logic
   * @private
   * @param {Array} rules - Permission rules to evaluate
   * @param {Object|null} schedule - Schedule object
   * @returns {boolean|Array} Combined permission options or false
   */
  _evaluatePrivilege(rules, schedule) {
    const results = [];

    rules.forEach(rule => {
      const permissionOptions = this._evaluateRule(rule, schedule);
      if (permissionOptions) {
        results.push(permissionOptions);
      }
    });

    if (results.length === 0) {
      return false;
    }

    // Combine permission options from all matching rules
    return results.flatMap(options => options);
  }

  /**
   * Check if user has permission for a specific action
   * @param {string} action - The action to check (e.g., 'scheduleCreate', 'scheduleEdit')
   * @param {Object|null} schedule - Optional schedule object to evaluate against
   *   Should have properties: { id, tags, teamId, createdByUserId, etc. }
   * @returns {boolean} Whether user has permission
   */
  hasPermission(action, schedule = null) {
    const rules = this.permissionRulesByActionMap[action] || [];

    if (rules.length === 0) {
      return false;
    }

    return !!this._evaluatePrivilege(rules, schedule);
  }

  /**
   * Get permission options for a specific action
   * @param {string} action - The action to check
   * @param {Object|null} schedule - Optional schedule object to evaluate against
   * @returns {Array} Array of permission options
   */
  getPermissionOptions(action, schedule = null) {
    const rules = this.permissionRulesByActionMap[action] || [];

    if (rules.length === 0) {
      return [];
    }

    const result = this._evaluatePrivilege(rules, schedule);
    return result || [];
  }

  /**
   * Check if user can create schedules
   * @returns {boolean}
   */
  canCreateSchedule() {
    return this.hasPermission('scheduleCreate');
  }

  /**
   * Check if user can read a specific schedule
   * @param {Object} schedule - Schedule object with at minimum { id }
   * @returns {boolean}
   */
  canReadSchedule(schedule) {
    if (!schedule || typeof schedule !== 'object') {
      throw new Error('schedule must be an object');
    }

    return this.hasPermission('scheduleRead', schedule);
  }

  /**
   * Check if user can edit a specific schedule
   * @param {Object} schedule - Schedule object
   * @returns {boolean}
   */
  canEditSchedule(schedule) {
    if (!schedule || typeof schedule !== 'object') {
      throw new Error('schedule must be an object');
    }

    return this.hasPermission('scheduleEdit', schedule);
  }

  /**
   * Check if user can enable a specific schedule
   * @param {Object} schedule - Schedule object
   * @returns {boolean}
   */
  canEnableSchedule(schedule) {
    if (!schedule || typeof schedule !== 'object') {
      throw new Error('schedule must be an object');
    }

    return this.hasPermission('scheduleEnable', schedule);
  }


  /**
   * Check if user has access to the schedule page
   * @returns {boolean}
   */
  canAccessSchedulePage() {
    return this.hasPermission('schedulePageAccess');
  }

  /**
   * Get all available schedule actions for a specific schedule
   * @param {Object|null} schedule - Schedule to check, or null for create-level actions
   * @returns {Object} Object with boolean flags for each action
   */
  getAvailableActions(schedule = null) {
    const actions = {
      canCreate: this.canCreateSchedule(),
      canAccessPage: this.canAccessSchedulePage(),
    };

    if (schedule && typeof schedule === 'object') {
      actions.canRead = this.canReadSchedule(schedule);
      actions.canEdit = this.canEditSchedule(schedule);
      actions.canEnable = this.canEnableSchedule(schedule);
    }

    return actions;
  }

  // ============================================
  // Subtask Permission Methods
  // ============================================

  /**
   * Check if user can link/unlink a subtask to issues or processes
   *
   * Subtask link permission supports these conditions:
   * - schedule: Specific schedule ID
   * - scheduleTag: Schedule tags (uses subtask.tags)
   * - matchesMyTeamTag: Matches user's team tags
   * - leadByMyTeam: Subtask is led by user's team (uses subtask.leadByTeamId)
   *
   * @param {Object} subtask - Subtask object with properties:
   *   - scheduleId: The schedule this subtask belongs to
   *   - tags: Schedule tags (inherited from parent schedule)
   *   - leadByTeamId: Team ID that leads this subtask
   * @returns {boolean}
   */
  canLinkSubtask(subtask) {
    if (!subtask || typeof subtask !== 'object') {
      throw new Error('subtask must be an object');
    }

    return this.hasPermission('subtaskLink', subtask);
  }

  /**
   * Check if user can change the lead team of a subtask
   *
   * Subtask change lead permission supports the same conditions as subtaskLink:
   * - schedule: Specific schedule ID
   * - scheduleTag: Schedule tags (uses subtask.tags)
   * - matchesMyTeamTag: Matches user's team tags
   * - leadByMyTeam: Subtask is led by user's team
   *
   * Additionally, subtaskChangeLead has permission options:
   * - teamTag: Restricts which team tags can be assigned as lead
   *
   * @param {Object} subtask - Subtask object
   * @returns {boolean}
   */
  canChangeSubtaskLead(subtask) {
    if (!subtask || typeof subtask !== 'object') {
      throw new Error('subtask must be an object');
    }

    return this.hasPermission('subtaskChangeLead', subtask);
  }

  /**
   * Get permission options for changing subtask lead
   * Returns team tag restrictions if any are configured
   *
   * @param {Object} subtask - Subtask object
   * @returns {Array} Array of permission options, e.g., [{ type: 'teamTag', value: ['tag1', 'tag2'] }]
   */
  getSubtaskChangeLeadOptions(subtask) {
    if (!subtask || typeof subtask !== 'object') {
      throw new Error('subtask must be an object');
    }

    return this.getPermissionOptions('subtaskChangeLead', subtask);
  }

  /**
   * Get all available subtask actions for a specific subtask
   * @param {Object} subtask - Subtask object
   * @returns {Object} Object with boolean flags for each subtask action
   */
  getAvailableSubtaskActions(subtask) {
    if (!subtask || typeof subtask !== 'object') {
      throw new Error('subtask must be an object');
    }

    return {
      canLink: this.canLinkSubtask(subtask),
      canChangeLead: this.canChangeSubtaskLead(subtask),
    };
  }

  /**
   * Check if user can access the Link page
   *
   * When called with an array of schedules:
   * - Returns true if at least one schedule matches the subtaskLink rules
   *
   * When called with a single schedule object:
   * - Returns true if the schedule matches any subtaskLink rule's conditions
   *
   * When called without arguments:
   * - Returns true if user has full subtask link access (rule with no conditions)
   *
   * @param {Array|Object|null} schedules - Array of schedules, single schedule object, or null
   * @returns {boolean}
   */
  canAccessLinkPage(schedules = null) {
    const rules = this.permissionRulesByActionMap['subtaskLink'] || [];

    if (rules.length === 0) {
      return false;
    }

    // Check if user is on their default team (subtaskLink is team-specific)
    if (this.teamId !== this.defaultTeamId) {
      return false;
    }

    // If an array of schedules is provided, check if at least one matches
    if (Array.isArray(schedules)) {
      return schedules.some(schedule => this._scheduleMatchesSubtaskLinkRules(schedule));
    }

    // If a single schedule is provided, check if it matches any subtaskLink rule
    if (schedules && typeof schedules === 'object') {
      return this._scheduleMatchesSubtaskLinkRules(schedules);
    }

    // No schedules provided - check for full access (rule with no conditions)
    const hasFullAccess = rules.some(rule => {
      const conditions = rule.permissionConditions || [];
      return conditions.length === 0;
    });

    return hasFullAccess;
  }

  /**
   * Check if a schedule matches any subtaskLink rule's conditions
   *
   * This evaluates 'schedule' (ID) and 'scheduleTag' conditions against the schedule.
   * Team-specific conditions (matchesMyTeamTag, leadByMyTeam) are ignored since
   * this is for admin-level schedule access checking.
   *
   * @private
   * @param {Object} schedule - Schedule object with { id, tags }
   * @returns {boolean}
   */
  _scheduleMatchesSubtaskLinkRules(schedule) {
    const rules = this.permissionRulesByActionMap['subtaskLink'] || [];

    for (const rule of rules) {
      const conditions = rule.permissionConditions || [];

      // If no conditions, user has full access - schedule matches
      if (conditions.length === 0) {
        return true;
      }

      // Filter to only schedule-level conditions (ignore team-specific ones)
      const scheduleLevelConditions = conditions.filter(c =>
        ['schedule', 'scheduleTag'].includes(c.type)
      );

      // If rule only has team-specific conditions, it grants access to all schedules
      // for admin purposes (we're ignoring team conditions)
      if (scheduleLevelConditions.length === 0) {
        return true;
      }

      // Check if all schedule-level conditions are met
      const allScheduleConditionsMet = scheduleLevelConditions.every(condition => {
        if (condition.type === 'schedule') {
          return this._evaluate(condition.value, schedule.id);
        }
        if (condition.type === 'scheduleTag') {
          return this._evaluate(condition.value, schedule.tags);
        }
        return true;
      });

      if (allScheduleConditionsMet) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get linkable schedule info for admin purposes
   *
   * This ignores conditions like 'matchesMyTeamTag' or 'leadByMyTeam' since
   * this is an admin app for managing a whole schedule.
   *
   * Returns an object with:
   * - scheduleIds: Set of specific schedule IDs that are allowed
   * - scheduleTags: Array of tag conditions that schedules must match
   * - hasFullAccess: Whether user has unrestricted access to all schedules
   *
   * @private
   * @returns {Object} { scheduleIds: Set, scheduleTags: Array, hasFullAccess: boolean }
   */
  _getLinkableScheduleInfo() {
    const rules = this.permissionRulesByActionMap['subtaskLink'] || [];
    const scheduleIds = new Set();
    const scheduleTags = [];
    let hasFullAccess = false;

    for (const rule of rules) {
      const conditions = rule.permissionConditions || [];

      // If no conditions, user has access to all schedules
      if (conditions.length === 0) {
        hasFullAccess = true;
        continue;
      }

      // Filter to only schedule-level conditions
      const scheduleLevelConditions = conditions.filter(c =>
        ['schedule', 'scheduleTag'].includes(c.type)
      );

      // If there are only team-specific conditions (no schedule-level conditions),
      // this rule grants access to all schedules for admin purposes
      if (scheduleLevelConditions.length === 0) {
        hasFullAccess = true;
        continue;
      }

      // Collect schedule IDs from 'schedule' conditions
      const scheduleConditions = conditions.filter(c => c.type === 'schedule');
      for (const condition of scheduleConditions) {
        if (Array.isArray(condition.value)) {
          condition.value.forEach(id => scheduleIds.add(id));
        } else if (condition.value) {
          scheduleIds.add(condition.value);
        }
      }

      // Collect tag conditions from 'scheduleTag' conditions
      const tagConditions = conditions.filter(c => c.type === 'scheduleTag');
      for (const condition of tagConditions) {
        scheduleTags.push(condition.value);
      }
    }

    return { scheduleIds, scheduleTags, hasFullAccess };
  }

  /**
   * Get schedule IDs that the user can link subtasks in for admin purposes
   *
   * @deprecated Use _getLinkableScheduleInfo() for full tag support
   * @private
   * @returns {Set} Set of schedule IDs (or Set with '*' for full access)
   */
  _getLinkableScheduleIds() {
    const { scheduleIds, hasFullAccess } = this._getLinkableScheduleInfo();

    if (hasFullAccess) {
      return new Set(['*']);
    }

    return scheduleIds;
  }

  /**
   * Filter schedules to only those the user can link subtasks in
   *
   * For admin app purposes, this ignores team-specific conditions
   * (matchesMyTeamTag, leadByMyTeam) and only considers schedule-level restrictions
   * including both 'schedule' (ID) and 'scheduleTag' conditions.
   *
   * @param {Array} schedules - Array of schedule objects with at minimum { id, tags }
   * @returns {Array} Filtered array of schedules
   */
  filterLinkableSchedules(schedules) {
    if (!Array.isArray(schedules)) {
      return [];
    }

    // Check if user is on their default team (subtaskLink is team-specific)
    if (this.teamId !== this.defaultTeamId) {
      return [];
    }

    const { hasFullAccess } = this._getLinkableScheduleInfo();

    // If user has access to all schedules
    if (hasFullAccess) {
      return schedules;
    }

    // Filter schedules using the comprehensive rule matching
    return schedules.filter(schedule => this._scheduleMatchesSubtaskLinkRules(schedule));
  }
}
